import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Controller per gli hotspots dinamici: cambia l'immagine di sfondo della sezione
 * al cambio variante (lato client, senza reload).
 *
 * Riusa gli output dei task Liquid precedenti:
 * - `ref="backgroundImage"` → <img class="hotspots__background-image"> (Task 1).
 * - `ref="variantImages"`   → <script type="application/json"> con la mappa
 *   `variant_id → { url, srcset, width, height, alt }` (Task 2).
 *
 * @typedef {object} VariantImage
 * @property {string} url
 * @property {string} [srcset]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [alt]
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} backgroundImage - L'immagine di sfondo da aggiornare.
 * @property {HTMLScriptElement} variantImages - Lo script JSON con la mappa varianti→immagine.
 *
 * @extends Component<Refs>
 */
export class ProductHotspotsDynamicComponent extends Component {
  requiredRefs = ['backgroundImage', 'variantImages'];

  /**
   * Mappa `variant_id → VariantImage`, parsata pigramente dallo <script> JSON.
   * @type {Record<string, VariantImage> | null}
   */
  #variantImageMap = null;

  connectedCallback() {
    super.connectedCallback();

    // L'evento di cambio variante è dispatchato su `document` (vedi events.js).
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup del listener aggiunto manualmente in connectedCallback.
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Legge (una sola volta) e mette in cache la mappa varianti→immagine.
   * @returns {Record<string, VariantImage> | null}
   */
  #getVariantImageMap() {
    if (this.#variantImageMap) return this.#variantImageMap;

    const { variantImages } = this.refs;
    const json = variantImages?.textContent;
    if (!json) return null;

    try {
      this.#variantImageMap = /** @type {Record<string, VariantImage>} */ (JSON.parse(json));
    } catch (error) {
      console.error('[product-hotspots-dynamic] Mappa immagini varianti non valida', error);
      this.#variantImageMap = null;
    }

    return this.#variantImageMap;
  }

  /**
   * Aggiorna `src`/`srcset` dell'immagine di sfondo in base alla variante selezionata.
   * Fallback: se la variante non è nella mappa, lascia l'immagine corrente.
   *
   * @param {Event & { detail?: { resource?: { id?: string | number } } }} event
   * @returns {void}
   */
  #handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (variantId == null) return;

    const map = this.#getVariantImageMap();
    if (!map) return;

    const image = map[String(variantId)];
    if (!image?.url) return; // Variante senza immagine in mappa → lascia l'immagine corrente.

    const { backgroundImage } = this.refs;
    if (!(backgroundImage instanceof HTMLImageElement)) return;

    backgroundImage.src = image.url;

    if (image.srcset) {
      backgroundImage.srcset = image.srcset;
    } else {
      backgroundImage.removeAttribute('srcset');
    }

    if (typeof image.alt === 'string') {
      backgroundImage.alt = image.alt;
    }
  };
}

// Registrazione del custom element (il wiring nella sezione è il Task 4).
customElements.define('product-hotspots-dynamic-component', ProductHotspotsDynamicComponent);
